import { useQuery } from "@tanstack/react-query";
import { apiV1 } from "api-v2";
import { useCurrentCompanyId } from "hooks/auth";
import type { UserListFilters, UserListResponse } from "../../../types/userInvite";

const fetchUsers = async (companyId: number, filters: UserListFilters): Promise<UserListResponse> => {
  const params: Record<string, string | string[] | number> = {};
  if (filters.status && filters.status.length > 0) {
    params["status[]"] = filters.status;
  }
  if (filters.search) {
    params.search = filters.search;
  }
  if (filters.sort) {
    params.sort = filters.sort;
  }
  if (filters.page) {
    params.page = filters.page;
  }
  const response = await apiV1.get<UserListResponse>(`companies/${companyId}/users`, { params });
  return response.data;
};

interface UseUsersQueryOptions {
  /*
   * PRO-709: false when the caller's RBAC capability denies user management.
   * The endpoint currently answers 200 for non-admins, so the list would
   * otherwise be fetched — and briefly rendered — for a user who must not see
   * it. Defaults true, leaving every existing call site unchanged.
   */
  enabled?: boolean;
}

export const useUsersQuery = (
  filters: UserListFilters = {},
  { enabled = true }: UseUsersQueryOptions = {}
) => {
  const companyId = useCurrentCompanyId();

  return useQuery<UserListResponse, Error>({
    queryKey: ["users", companyId, filters],
    queryFn: () => fetchUsers(companyId!, filters),
    enabled: enabled && companyId != null,
  });
};
